import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AIProgramCreator from '../components/PurchaseProgram/AIProgramCreator';
import ProgramCreationWizard from '../components/PurchaseProgram/ProgramCreationWizard';
import ConfirmPopup from '../components/PurchaseProgram/ConfirmPopup';
import {
  deleteAdminProgram,
  getAdminProgram,
  listAdminPrograms,
  listProductLists,
  listUserGroups,
  updateAdminProgram,
} from '../services/purchaseProgramApi';

function programToWizardCustomerSite(program) {
  const assign = program.assignments || [];
  const shipKeys = assign.filter((a) => a.entity_type === 'ship_to').map((a) => String(a.entity_key));
  const custKeys = assign.filter((a) => a.entity_type === 'customer' && a.entity_key !== 'self').map((a) => String(a.entity_key));
  const userKeys = assign.filter((a) => a.entity_type === 'user').map((a) => String(a.entity_key));
  const delKeys = assign.filter((a) => a.entity_type === 'delivery_ship_to').map((a) => String(a.entity_key));
  let restriction_basis = 'users';
  if (delKeys.length && userKeys.length === 0) restriction_basis = 'delivery';
  return {
    ship_to_csv: shipKeys.join(', '),
    customer_csv: custKeys.join(', '),
    selectedSite: null,
    selectedCustomers: [],
    user_groups_csv: assign.filter((a) => a.entity_type === 'user_group').map((a) => a.entity_key).join(', '),
    user_scope_mode: userKeys.length ? 'select' : 'all',
    selected_users: userKeys.map((id) => ({ id, customer_id: '' })),
    restriction_basis,
    delivery_scope_mode: restriction_basis === 'delivery' ? (delKeys.length ? 'select' : 'all') : 'all',
    delivery_ship_to_csv: delKeys.join(', '),
  };
}

function wizardPeriodTypeFromProgram(p) {
  if (!p?.period_type || p.period_type === 'none') return 'none';
  if (p.period_type === 'org_interval') return 'days';
  if (p.period_type === 'user_hire_date') return 'hire_date';
  return 'none';
}

function wizardLimitTypeFromProgram(p) {
  const hasAmt = p.amount_limit != null && Number(p.amount_limit) > 0;
  const hasQty = p.item_limit != null && Number(p.item_limit) > 0;
  if (hasAmt && hasQty) return 'both';
  if (hasAmt) return 'dollar';
  if (hasQty) return 'qty';
  return 'none';
}

function programToWizardAnswers(p, productLists) {
  if (!p) return {};
  const listLimits = (p.item_limits || []).filter(r => r.limit_type === 'list');
  const productListSelections = (p.product_list_ids || []).map(id => {
    const rule = listLimits.find(r => r.list_id === id);
    const pl = productLists.find(l => l.id === id);
    return { list_id: id, list_name: pl?.list_name || rule?.list_name || `List #${id}`, max_qty: rule?.max_qty ?? 1 };
  });
  return {
    name: p.name || '',
    description: p.description || '',
    is_active: p.is_active ?? true,
    start_date: p.start_date?.slice(0, 10) || '',
    end_date: p.end_date?.slice(0, 10) || '',
    amount_limit: p.amount_limit ?? null,
    item_limit: p.item_limit ?? null,
    limit_type: wizardLimitTypeFromProgram(p),
    period_type: wizardPeriodTypeFromProgram(p),
    period_interval_days: p.period_interval_days ?? null,
    period_anchor_date: p.period_anchor_date?.slice(0, 10) || '',
    product_list_selections: productListSelections,
    customer_site: programToWizardCustomerSite(p),
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────
const MODES = [
  { key: 'wizard', icon: 'bi-ui-checks-grid', color: '#0891b2', bg: '#e0f2fe', label: 'Guided Wizard', desc: 'Step-by-step with current settings pre-filled.' },
  { key: 'ai', icon: 'bi-stars', color: '#6366f1', bg: '#ede9fe', label: 'AI Assistant', desc: 'Chat to update your program. History saved.' },
];

// ─── Mode tab bar (sticky, always visible once in a mode) ─────────────────────
function ModeTabBar({ current, onSwitch }) {
  return (
    <div style={{ display: 'flex', gap: 6, padding: '8px 0 0', borderTop: '1px solid #f3f4f6', marginTop: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', alignSelf: 'center', marginRight: 4 }}>
        Edit using:
      </span>
      {MODES.map(({ key, icon, color, bg, label }) => {
        const active = current === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => !active && onSwitch(key)}
            aria-pressed={active}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500,
              border: `1.5px solid ${active ? color : '#e5e7eb'}`,
              background: active ? bg : '#fff',
              color: active ? color : '#6b7280',
              cursor: active ? 'default' : 'pointer',
              transition: 'all .15s',
            }}
            onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = bg; e.currentTarget.style.color = color; } }}
            onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6b7280'; } }}
          >
            <i className={`bi ${icon}`} aria-hidden="true" />
            {label}
            {active && <i className="bi bi-check2" style={{ fontSize: 13 }} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ProgramEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('wizard');
  const [pendingMode, setPendingMode] = useState(null); // mode switch pending confirmation
  const [program, setProgram] = useState(null);
  const [productLists, setProductLists] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAdminProgram(id).catch(() => listAdminPrograms().then(r => ({ ...(r?.items || []).find(p => String(p.id) === String(id)) }))),
      listProductLists(),
      listUserGroups(),
    ]).then(([prog, pl, ug]) => {
      setProgram(prog?.id ? prog : null);
      setProductLists(pl?.items || []);
      setUserGroups(ug?.items || []);
    }).catch(() => toast.error('Failed to load program')).finally(() => setLoading(false));
  }, [id]);

  const handleSave = useCallback(async (payload) => {
    try {
      await updateAdminProgram(id, payload);
      toast.success('Program saved!');
      navigate('/purchase-program');
    } catch (err) {
      toast.error(err.message || 'Failed to save program');
    }
  }, [id, navigate]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAdminProgram(id);
      toast.success('Program deleted');
      navigate('/purchase-program');
    } catch (err) {
      toast.error(err.message || 'Failed to delete program');
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [id, navigate]);

  // Request a mode switch — shows confirm if mid-edit (AI has its own state, wizard is stateful)
  const requestSwitch = (next) => {
    if (next === mode) return;
    setPendingMode(next);
  };

  const confirmSwitch = () => {
    setMode(pendingMode);
    setPendingMode(null);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner-border text-primary" style={{ width: 36, height: 36 }} />
      </div>
    );
  }

  if (!program) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', padding: 40, textAlign: 'center' }}>
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 40, color: '#f59e0b', display: 'block', marginBottom: 12 }} />
        <p style={{ fontSize: 16, color: '#374151' }}>Program not found.</p>
        <NavLink to="/purchase-program" className="btn btn-outline-secondary btn-sm">Back to Programs</NavLink>
      </div>
    );
  }

  const wizardAnswers = programToWizardAnswers(program, productLists);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>

      {/* ── Sticky header ─────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px 0', position: 'sticky', top: 0, zIndex: 100 }}>
        <NavLink to="/purchase-program" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-arrow-left" /> Back to Programs
        </NavLink>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
          {/* Title */}
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <i className="bi bi-pencil-square" style={{ color: '#1a56db', fontSize: 18 }} aria-hidden="true" />
              Edit: {program.name}
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: program.is_active ? '#d1fae5' : '#f3f4f6', color: program.is_active ? '#065f46' : '#6b7280', marginLeft: 4 }}>
                <i className={`bi ${program.is_active ? 'bi-lightning-charge-fill' : 'bi-pause-circle'} me-1`} />
                {program.is_active ? 'Active' : 'Inactive'}
              </span>
            </h1>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>ID: {program.id}</div>
          </div>

          {/* Delete button */}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => setConfirmDelete(true)}
            style={{ fontWeight: 600, height: 36 }}
            aria-label="Delete program"
          >
            <i className="bi bi-trash3 me-1" aria-hidden="true" />Delete Program
          </button>
        </div>

        {/* Mode tab bar — always visible */}
        <ModeTabBar current={mode} onSwitch={requestSwitch} />
      </div>

      {/* ── Mode content ──────────────────────────────────────────────── */}

      {mode === 'wizard' && (
        <div style={{ maxWidth: 700, margin: '32px auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.08)', overflow: 'hidden', minHeight: 500 }}>
          <ProgramCreationWizard
            productLists={productLists}
            userGroups={userGroups}
            initialAnswers={wizardAnswers}
            onFinish={handleSave}
            onCancel={() => navigate('/purchase-program')}
          />
        </div>
      )}

      {mode === 'ai' && (
        <div style={{ maxWidth: 780, margin: '32px auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.08)', overflow: 'hidden', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
          <AIProgramCreator
            productLists={productLists}
            userGroups={userGroups}
            editingProgram={program}
            onFinish={handleSave}
            onCancel={() => navigate('/purchase-program')}
          />
        </div>
      )}

      {/* ── Switch-mode confirmation ───────────────────────────────────── */}
      <ConfirmPopup
        open={!!pendingMode}
        title="Switch editing method?"
        message={`Your unsaved changes in ${MODES.find(m => m.key === mode)?.label || 'this mode'} will be discarded. Switch to ${MODES.find(m => m.key === pendingMode)?.label || ''}?`}
        confirmLabel="Yes, switch"
        onConfirm={confirmSwitch}
        onCancel={() => setPendingMode(null)}
      />

      {/* ── Delete confirmation ────────────────────────────────────────── */}
      <ConfirmPopup
        open={confirmDelete}
        title="Delete this program?"
        message={`"${program.name}" and all associated data will be permanently removed. This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete Program'}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
